import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraFormField, SpecraInput, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { audienceKey, useAudienceDirectory } from '../Library/audienceDirectory';
import { HistoryLink } from '../Library/ReleaseHistory';
import { libraryReleaseClient } from '../Library/reuseClient';
const ids = Testids.customerContext;
/** Authoring starts empty: no implicit selection of notes, evidence or siblings. */ export function CustomerContextRelease({ captureProjectId, sourceKind, sourceId }) {
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [summary, setSummary] = useState('');
    const [query, setQuery] = useState('');
    const [audience, setAudience] = useState();
    const inFlight = useRef(false);
    const [preview, setPreview] = useState();
    const [released, setReleased] = useState();
    const directory = useAudienceDirectory(query, open && !released);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const request = ()=>({
            captureProjectId,
            sourceKind,
            sourceId,
            title,
            summary,
            audiences: audience ? [
                {
                    kind: audience.kind,
                    scopeNodeId: audience.scopeNodeId,
                    principalGroupId: audience.principalGroupId
                }
            ] : []
        });
    const resetPreview = ()=>{
        setPreview(undefined);
        setError('');
    };
    const act = async (release)=>{
        if (inFlight.current || released || !audience) return;
        if (release && (!preview || !preview.audiences.length || preview.audiences.some((a)=>audienceKey(a) !== audience.key))) return;
        inFlight.current = true;
        setBusy(true);
        setError('');
        try {
            if (release && preview) {
                const result = await libraryReleaseClient.releaseCustomerContext({
                    ...request(),
                    previewToken: preview.previewToken
                });
                setReleased(result.summary);
                setPreview(undefined);
            } else {
                setPreview(await libraryReleaseClient.previewCustomerContext(request()));
            }
        } catch  {
            setPreview(undefined);
            setError('Could not share this insight. Check the source, your permission to share and the audience, then preview again. Your text is kept.');
        } finally{
            inFlight.current = false;
            setBusy(false);
        }
    };
    if (!open) return /*#__PURE__*/ _jsx(SpecraButton, {
        variant: "secondary",
        size: "sm",
        "data-testid": ids.share,
        onClick: ()=>setOpen(true),
        children: "Share insight"
    });
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-testid": ids.editor,
        children: [
            /*#__PURE__*/ _jsx(SpecraCardHeader, {
                children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                    children: "Share selected insight"
                })
            }),
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "meta",
                        children: "Only the summary below is shared with the chosen internal audience. Raw notes, files and other themes stay where they are. This does not allow public or government sharing."
                    }),
                    !released && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Shared title",
                                children: /*#__PURE__*/ _jsx(SpecraInput, {
                                    "aria-label": "Shared title",
                                    "data-testid": ids.title,
                                    value: title,
                                    maxLength: 512,
                                    disabled: busy,
                                    onChange: (e)=>{
                                        setTitle(e.target.value);
                                        resetPreview();
                                    }
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Bounded summary",
                                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                    "aria-label": "Bounded summary",
                                    "data-testid": ids.summary,
                                    value: summary,
                                    maxLength: 4000,
                                    disabled: busy,
                                    onChange: (e)=>{
                                        setSummary(e.target.value);
                                        resetPreview();
                                    }
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Find an internal audience",
                                hint: "Choose an existing internal audience by name.",
                                children: /*#__PURE__*/ _jsx(SpecraInput, {
                                    "aria-label": "Find an internal audience",
                                    "data-testid": ids.audienceSearch,
                                    value: query,
                                    disabled: busy,
                                    onChange: (e)=>setQuery(e.target.value)
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Intended audience",
                                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                                    "aria-label": "Intended audience",
                                    "data-testid": ids.audience,
                                    value: audience?.key || '',
                                    disabled: busy || directory.loading || directory.error,
                                    onChange: (e)=>{
                                        setAudience(directory.choices.find((a)=>a.key === e.target.value));
                                        resetPreview();
                                    },
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "",
                                            children: "Select an internal audience"
                                        }),
                                        audience && !directory.choices.some((a)=>a.key === audience.key) && /*#__PURE__*/ _jsxs("option", {
                                            value: audience.key,
                                            children: [
                                                audience.name,
                                                " · ",
                                                audience.kind === 'scope_node' ? 'Scope' : 'Group'
                                            ]
                                        }),
                                        directory.choices.map((a)=>/*#__PURE__*/ _jsxs("option", {
                                                value: a.key,
                                                children: [
                                                    a.name,
                                                    " · ",
                                                    a.kind === 'scope_node' ? 'Scope' : 'Group'
                                                ]
                                            }, a.key))
                                    ]
                                })
                            }),
                            directory.loading && /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                "aria-live": "polite",
                                children: "Loading authorized audiences…"
                            }),
                            directory.error && /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                role: "alert",
                                children: "Audiences are unavailable. Check your directory access and retry."
                            }),
                            !directory.loading && !directory.error && directory.choices.length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                children: "No matching authorized audiences. Try another name or ask your administrator about access."
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "ghost",
                                "data-testid": ids.audienceRetry,
                                disabled: busy || directory.loading,
                                onClick: directory.retry,
                                children: "Refresh audiences"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": ids.preview,
                                disabled: busy || directory.loading || directory.error || !title.trim() || !summary.trim() || !audience,
                                onClick: ()=>void act(false),
                                children: busy ? 'Checking…' : 'Preview sharing'
                            }),
                            preview && /*#__PURE__*/ _jsx(SpecraCard, {
                                "data-testid": ids.previewContent,
                                children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
                                    className: "space-y-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraText, {
                                            as: "p",
                                            className: "font-medium",
                                            children: preview.title
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraText, {
                                            as: "p",
                                            className: "whitespace-pre-wrap",
                                            children: preview.summary
                                        }),
                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                            children: [
                                                "Intended audience:",
                                                ' ',
                                                preview.audiences.map((a)=>audienceKey(a) === audience?.key ? audience.name : 'Audience unavailable — preview again').join(', ')
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraText, {
                                            as: "p",
                                            size: "meta",
                                            children: "Source: Growth customer insight. Confirming records today as the reviewed date."
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            "data-testid": ids.confirm,
                                            disabled: busy || !preview.audiences.length || preview.audiences.some((a)=>audienceKey(a) !== audience?.key),
                                            onClick: ()=>void act(true),
                                            children: "Confirm sharing"
                                        })
                                    ]
                                })
                            })
                        ]
                    }),
                    error && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        role: "alert",
                        children: error
                    }),
                    released && /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        "data-testid": ids.success,
                        "aria-live": "polite",
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "p",
                                children: [
                                    "Shared version ",
                                    released.version,
                                    " ·",
                                    ' ',
                                    released.releasedAt?.toDate().toLocaleString() || 'Share date not recorded'
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "p",
                                children: [
                                    released.title,
                                    " · Shared with ",
                                    audience?.name
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Link, {
                                to: `/library?record=${encodeURIComponent(released.libraryItemId)}`,
                                "data-testid": ids.openReleased,
                                children: "Open shared insight"
                            }),
                            /*#__PURE__*/ _jsx(HistoryLink, {
                                itemId: released.libraryItemId
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "ghost",
                        disabled: busy,
                        onClick: ()=>{
                            setOpen(false);
                            resetPreview();
                        },
                        children: "Close sharing"
                    })
                ]
            })
        ]
    });
}
